import React, { useMemo } from 'react';
import { Box, Typography, Chip, Alert, Stack } from '@mui/material';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import type { CategoryMeta } from './categoryMeta';
import type { ExceptionDetail } from './ExceptionAnalysis.api';

interface ExceptionGridProps {
  meta: CategoryMeta;
  data: ExceptionDetail | null;
  loading: boolean;
  error: string | null;
}

// Rows arrive without a stable `id` — the DB views don't emit one. Synthesize
// one client-side from the row index; row order is stable per fetch since
// the backend doesn't paginate/sort.
const withRowIds = (rows: Record<string, any>[]) =>
  rows.map((r, i) => ({ id: i, ...r }));

const ExceptionGrid: React.FC<ExceptionGridProps> = ({ meta, data, loading, error }) => {
  const rows = useMemo(() => (data ? withRowIds(data.rows) : []), [data]);

  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="h6">{meta.label}</Typography>
          <Chip
            size="small"
            label={loading ? '…' : `${data?.count ?? 0} row${data?.count === 1 ? '' : 's'}`}
            color={data?.count && data.count > 0 ? 'default' : 'success'}
            sx={{ fontVariantNumeric: 'tabular-nums' }}
          />
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {meta.detects}
        </Typography>
      </Box>

      <Box sx={{ flex: 1, overflow: 'hidden', p: 2 }}>
        {error ? (
          <Alert severity="error">{error}</Alert>
        ) : (
          <DataGrid
            rows={rows}
            columns={meta.columns}
            loading={loading}
            density="compact"
            disableRowSelectionOnClick
            slots={{ toolbar: GridToolbar }}
            slotProps={{
              toolbar: {
                showQuickFilter: true,
                quickFilterProps: { debounceMs: 300 },
              },
            }}
            initialState={{
              pagination: { paginationModel: { pageSize: 50 } },
            }}
            pageSizeOptions={[25, 50, 100, 250]}
            sx={{
              '& .MuiDataGrid-cell': { fontSize: '0.85rem' },
              '& .MuiDataGrid-columnHeader': { fontSize: '0.85rem', fontWeight: 600 },
            }}
          />
        )}
      </Box>
    </Box>
  );
};

export default ExceptionGrid;
